import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  BabyIcon,
  HeartPulse,
  Loader2,
  MessageSquarePlus,
  Phone,
  Send,
  Stethoscope,
  Users,
} from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { teamApi, type AgentRoleOption, type AgentThread } from "@/features/team/team.api";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

const roleIconMap: Record<string, typeof Phone> = {
  family: Users,
  nursing: HeartPulse,
  resident_senior: Stethoscope,
  attending: Stethoscope,
  specialist_cardiology: HeartPulse,
  specialist_pediatrics: BabyIcon,
  specialist_icu: HeartPulse,
};

function iconForRole(role: string) {
  return roleIconMap[role] ?? Phone;
}

export function TeamPanel({ encounterId, disabled }: { encounterId: string; disabled: boolean }) {
  const qc = useQueryClient();
  const roles = useQuery({
    queryKey: ["team-roles", encounterId],
    queryFn: () => teamApi.listRoles(encounterId),
  });
  const threads = useQuery({
    queryKey: ["team-threads", encounterId],
    queryFn: () => teamApi.listThreads(encounterId),
  });
  const [activeId, setActiveId] = useState<string | null>(null);
  const [message, setMessage] = useState("");

  const openMut = useMutation({
    mutationFn: (role: AgentRoleOption) =>
      teamApi.openThread(encounterId, {
        kind: role.default_kind,
        agent_role: role.value,
      }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["team-threads", encounterId] });
      setActiveId(t.id);
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Error abriendo thread"),
  });

  const sendMut = useMutation({
    mutationFn: ({ threadId, m }: { threadId: string; m: string }) =>
      teamApi.sendMessage(encounterId, threadId, m),
    onSuccess: () => {
      setMessage("");
      qc.invalidateQueries({ queryKey: ["team-threads", encounterId] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : "Error enviando"),
  });

  const closeMut = useMutation({
    mutationFn: (threadId: string) => teamApi.closeThread(encounterId, threadId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["team-threads", encounterId] });
    },
  });

  const active: AgentThread | undefined = threads.data?.find((t) => t.id === activeId);

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <div className="flex items-center gap-2 font-[var(--font-display)] text-sm font-bold">
          <Users size={14} className="text-[var(--color-blue-soft)]" />
          Equipo clínico
        </div>
        <p className="text-[11.5px] text-[var(--color-text-muted)]">
          Interconsulta, pase de visita, reporte de enfermería, reunión con familia.
        </p>
      </header>

      {!active && (
        <>
          <div>
            <div className="mb-1.5 font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
              Abrir nueva conversación
            </div>
            <div className="grid grid-cols-2 gap-1.5 md:grid-cols-3">
              {roles.data?.map((r) => {
                const Icon = iconForRole(r.value);
                return (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => openMut.mutate(r)}
                    disabled={disabled || openMut.isPending}
                    className="flex flex-col items-center gap-1 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)]/60 px-2 py-3 text-[11px] transition-colors hover:border-[var(--color-blue)]/40 hover:bg-[var(--color-surface-2)] disabled:opacity-50"
                  >
                    <Icon size={14} className="text-[var(--color-blue-soft)]" />
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-[var(--color-text-secondary)]">
                      {r.name.split(" ")[0]}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {threads.data && threads.data.length > 0 && (
            <div>
              <div className="mb-1.5 font-[var(--font-mono)] text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
                Threads abiertos
              </div>
              <ul className="space-y-1">
                {threads.data.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => setActiveId(t.id)}
                      className="group flex w-full items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)]/50 px-3 py-2 text-left text-[12px] hover:border-[var(--color-blue)]/40"
                    >
                      <MessageSquarePlus
                        size={12}
                        className="text-[var(--color-blue-soft)]"
                      />
                      <span className="flex-1 truncate">{t.title}</span>
                      <Badge variant={t.closed ? "stable" : "info"} size="sm">
                        {t.closed ? "cerrado" : `${t.messages.length} msg`}
                      </Badge>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {active && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex flex-col gap-3"
        >
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setActiveId(null)}>
              ← Volver
            </Button>
            <span className="truncate text-[13px] font-semibold">{active.title}</span>
            <Badge variant={active.closed ? "stable" : "info"} size="sm" className="ml-auto">
              {active.closed ? "cerrado" : "abierto"}
            </Badge>
          </div>

          <div className="space-y-2 max-h-[420px] overflow-y-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)]/40 p-3">
            {active.messages.length === 0 && (
              <div className="p-2 text-center text-[12px] text-[var(--color-text-muted)]">
                Inicia la conversación escribiendo abajo.
              </div>
            )}
            {active.messages.map((m) => (
              <div
                key={m.id}
                className={cn(
                  "flex",
                  m.sender === "student" ? "justify-end" : "justify-start",
                )}
              >
                <div
                  className={cn(
                    "max-w-[85%] rounded-xl border px-3 py-2 text-[13px]",
                    m.sender === "student"
                      ? "border-[var(--color-blue)]/25 bg-[var(--color-blue)]/10"
                      : "border-[var(--color-border)] bg-[var(--color-surface-2)]",
                  )}
                >
                  <div className="font-[var(--font-mono)] text-[9px] uppercase tracking-widest text-[var(--color-text-muted)]">
                    {m.sender === "student" ? "Vos" : active.agent_role.replace("specialist_", "").replace("_", " ")}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap">{m.content}</div>
                </div>
              </div>
            ))}
          </div>

          {!active.closed && (
            <div className="flex gap-2">
              <Input
                placeholder="Escribe al agente..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && message.length > 1 && !sendMut.isPending) {
                    sendMut.mutate({ threadId: active.id, m: message });
                  }
                }}
                disabled={disabled || sendMut.isPending}
              />
              <Button
                onClick={() =>
                  sendMut.mutate({ threadId: active.id, m: message })
                }
                disabled={disabled || sendMut.isPending || message.length < 1}
              >
                {sendMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              </Button>
            </div>
          )}

          {!active.closed && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => closeMut.mutate(active.id)}
              className="self-end text-[11px]"
            >
              Cerrar thread
            </Button>
          )}
        </motion.div>
      )}
    </div>
  );
}
